<script setup lang="ts">
type EventPaletteButtonLayer = 'far' | 'background' | 'portrait' | 'foreground'

withDefaults(defineProps<{
  label: string
  icon?: string
  iconText?: string
  layer?: EventPaletteButtonLayer
  showIcon?: boolean
  compact?: boolean
}>(), {
  icon: undefined,
  iconText: '',
  layer: undefined,
  showIcon: false,
  compact: false,
})

defineEmits<{
  click: [event: MouseEvent]
}>()
</script>

<template>
  <button
    type="button"
    class="palette-action-btn"
    :class="{ 'palette-action-btn--compact': compact }"
    :title="label"
    @click="$emit('click', $event)"
  >
    <span
      v-if="showIcon"
      class="palette-action-btn__icon-wrap"
      aria-hidden="true"
    >
      <span
        v-if="layer"
        class="palette-layer-icon"
        :class="`palette-layer-icon--${layer}`"
      >
        <i class="palette-layer-icon__frame" />
        <i class="palette-layer-icon__far" />
        <i class="palette-layer-icon__mountain" />
        <i class="palette-layer-icon__person" />
        <i class="palette-layer-icon__front" />
      </span>
      <svg
        v-else-if="icon"
        class="palette-action-btn__icon"
        viewBox="0 0 24 24"
      >
        <template v-if="icon === 'textTool'">
          <path d="M5 6h14M12 6v12M8.5 18h7" />
        </template>
        <template v-else-if="icon === 'dialogOffTool'">
          <path d="M6 7h12v8H9l-3 3V7Z" />
          <path d="M5 5l14 14" />
        </template>
        <template v-else-if="icon === 'imageTool'">
          <rect x="5" y="6" width="14" height="11" />
          <path d="M7.5 15l3.2-3.4 2.4 2.4 1.6-1.8 2.8 2.8" />
        </template>
        <template v-else-if="icon === 'audioTool'">
          <path d="M9 15.5a2.5 2.5 0 1 1-2-2.45V6l9-2v9.5a2.5 2.5 0 1 1-2-2.45V7.3L9 8.4v7.1Z" />
        </template>
        <template v-else-if="icon === 'choiceTool'">
          <path d="M7 7h10M7 12h10M7 17h10" />
          <path d="M4.5 7h.1M4.5 12h.1M4.5 17h.1" />
        </template>
        <template v-else-if="icon === 'noteTool'">
          <path d="M7 5h8l3 3v11H7V5Z" />
          <path d="M15 5v4h4M9 12h6M9 15h4" />
        </template>
        <template v-else-if="icon === 'autoTool'">
          <path d="M8 7v10l8-5-8-5Z" />
        </template>
        <template v-else-if="icon === 'variableTool'">
          <path d="M5 17c3.5-7 5.5-7 9 0M8 7l8 10M16 7 8 17" />
        </template>
        <template v-else-if="icon === 'flowTool'">
          <path d="M6 6h4v4H6V6ZM14 14h4v4h-4v-4Z" />
          <path d="M10 8h3.5c1.5 0 2.5 1 2.5 2.5V14" />
        </template>
        <template v-else-if="icon === 'uiTool'">
          <rect x="5" y="6" width="14" height="12" />
          <path d="M5 10h14M9 6v12" />
          <path d="M11.5 13h4" />
        </template>
        <template v-else>
          <path d="M12 4l1.6 5 5 .4-3.9 3.2 1.2 5-3.9-2.7-3.9 2.7 1.2-5-3.9-3.2 5-.4L12 4Z" />
        </template>
      </svg>
      <span v-else class="palette-action-btn__mark">{{ iconText }}</span>
    </span>
    <span class="palette-action-btn__label">{{ label }}</span>
  </button>
</template>

<style scoped>
.palette-action-btn {
  width: 100%;
  min-height: 28px;
  padding: var(--app-space-xs) var(--app-space-sm);
  border: 0;
  color: var(--theme-text-heading);
  display: flex;
  align-items: center;
  gap: var(--app-space-sm);
  font-size: var(--app-font-size-sm);
  font-weight: 400;
  line-height: 1.2;
  text-align: left;
  background: color-mix(in srgb, var(--palette-accent, var(--theme-text-secondary)) 8%, var(--theme-bg-card));
  cursor: pointer;
  box-sizing: border-box;
}

.palette-action-btn--compact {
  height: 22px;
  min-height: 22px;
  padding: 0 1px;
  gap: 1px;
  font-size: var(--app-font-size-xs);
  line-height: 1;
  background: color-mix(in srgb, var(--palette-accent, var(--theme-text-secondary)) 16%, var(--theme-bg-panel));
}

.palette-action-btn:hover {
  background: color-mix(in srgb, var(--palette-accent, var(--theme-text-secondary)) 24%, var(--theme-bg-hover));
}

.palette-action-btn + .palette-action-btn {
  margin-top: var(--app-space-2xs);
}

.palette-action-btn__icon-wrap {
  position: relative;
  flex: 0 0 18px;
  width: 18px;
  height: 18px;
  color: var(--palette-accent, var(--theme-text-secondary));
}

.palette-action-btn__icon {
  width: 18px;
  height: 18px;
  fill: none;
  stroke: currentColor;
  stroke-width: 1.8;
  stroke-linecap: round;
  stroke-linejoin: round;
}

.palette-action-btn__mark {
  display: block;
  width: 18px;
  color: var(--theme-text-secondary);
  font-size: 9px;
  text-align: center;
}

.palette-action-btn__label {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.palette-action-btn:not(.palette-action-btn--compact) .palette-action-btn__label {
  white-space: normal;
}

.palette-layer-icon {
  position: relative;
  display: block;
  width: 18px;
  height: 18px;
  color: var(--palette-accent, var(--theme-text-secondary));
}

.palette-layer-icon i {
  position: absolute;
  display: block;
  box-sizing: border-box;
}

.palette-layer-icon__frame {
  left: 2px;
  top: 3px;
  width: 14px;
  height: 11px;
  border: 1px solid currentColor;
}

.palette-layer-icon__far {
  left: 4px;
  top: 5px;
  width: 10px;
  height: 2px;
  border-top: 1px solid currentColor;
}

.palette-layer-icon__mountain {
  left: 5px;
  top: 9px;
  width: 8px;
  height: 5px;
  border-left: 1px solid currentColor;
  border-top: 1px solid currentColor;
  transform: skewX(-20deg) rotate(22deg);
}

.palette-layer-icon__person {
  left: 7px;
  top: 6px;
  width: 4px;
  height: 8px;
  border: 1px solid currentColor;
}

.palette-layer-icon__person::before {
  position: absolute;
  left: 1px;
  top: -4px;
  width: 2px;
  height: 2px;
  background: currentColor;
  content: "";
}

.palette-layer-icon__front {
  left: 4px;
  bottom: 3px;
  width: 11px;
  height: 3px;
  border-top: 1px solid currentColor;
  background: color-mix(in srgb, currentColor 28%, transparent);
}

.palette-layer-icon--far .palette-layer-icon__mountain,
.palette-layer-icon--far .palette-layer-icon__person,
.palette-layer-icon--far .palette-layer-icon__front,
.palette-layer-icon--background .palette-layer-icon__far,
.palette-layer-icon--background .palette-layer-icon__person,
.palette-layer-icon--background .palette-layer-icon__front,
.palette-layer-icon--portrait .palette-layer-icon__far,
.palette-layer-icon--portrait .palette-layer-icon__mountain,
.palette-layer-icon--portrait .palette-layer-icon__front,
.palette-layer-icon--foreground .palette-layer-icon__far,
.palette-layer-icon--foreground .palette-layer-icon__mountain,
.palette-layer-icon--foreground .palette-layer-icon__person {
  display: none;
}
</style>
